import { format } from 'date-fns';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { SkateboardStore } from 'models/RunModel/Skateboard';
import { RunModel } from 'models/RunModel/RunModel';
import { useCallback, useEffect, useState } from 'react';
import SequencePlayer from './SequencePlayer';
import { Clock } from 'three';

const timeStamp2Time = (ts: number) => {
  return format(new Date(ts), 'ss:SS');
};

interface ControlsOverlayProps {
  store: SkateboardStore;
  record: RunModel;
  clock: Clock;
  hasVideo: boolean;
  videoActive: boolean;
  onVideoToggle: () => void;
  showRunStatsBox?: boolean;
  showFigurePopup?: boolean;
  showGForceDisplay?: boolean;
}

const ControlsOverlay = observer((props: ControlsOverlayProps) => {
  const {
    store,
    record,
    clock,
    hasVideo,
    videoActive,
    onVideoToggle,
    showRunStatsBox = true,
    showFigurePopup = false,
    showGForceDisplay = false,
  } = props;

  // Button-State direkt aus State-Machine ableiten
  const isPlaying = store.animationState === "playing" && store.positionState !== "end";

  // Animation am Ende: Button auf Pause
  useEffect(() => {
    if (store.positionState === "end" && store.animationState === "playing") {
      store.pause();
    }
  }, [store.positionState, store.animationState]);

  const onSliderChange = useCallback(
    (time: number) => {
      if (!isPlaying) store.frame = time;
    },
    [isPlaying, store]
  );

  const togglePlaying = () => {
    if (!isPlaying) {
      // Play: State-Machine verwenden
      store.play();
      // Animation aktivieren: Intervall setzen
      if (!store.interval) {
        if (store.timeStamps && store.timeStamps.length > 1) {
          const avgInterval = (store.timeStamps[store.timeStamps.length - 1] - store.timeStamps[0]) / (store.timeStamps.length - 1);
          store.setInterval(avgInterval > 0 ? avgInterval : 16);
        } else {
          store.setInterval(16);
        }
      }
      // Clock für Resume zurücksetzen - FrameSync kümmert sich um die korrekte Frame-Berechnung
      clock.elapsedTime = 0;
      
      // Resume-Event für FrameSync triggern (custom event)
      const resumeEvent = new CustomEvent('animationResume', { 
        detail: { frame: store.frame, time: 0 } 
      });
      window.dispatchEvent(resumeEvent);
      clock.start();
    } else {
      // Pause: State-Machine verwenden
      store.pause();
      store.setInterval(0);
      clock.stop();
    }
  };

  return (
    <SequencePlayer
      max={store.frameNum}
      onChange={onSliderChange}
      togglePlaying={togglePlaying}
      isPlaying={isPlaying}
      record={record}
      onKnobMoveStart={() => store.pause()}
      timeValue={timeStamp2Time(store.timeStamp ?? 0)}
      value={store.frame || 0}
      valueToMs={(step) => store.timeStamps[step]}
      toggleVideo={onVideoToggle}
      videoActive={videoActive}
      hasVideo={hasVideo}
      showRunStatsBox={showRunStatsBox}
      showFigurePopup={showFigurePopup}
      showGForceDisplay={showGForceDisplay}
    />
  );
});

export default ControlsOverlay;
